import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  PageContainer,
  Button,
  formatDate,
  fadeIn
} from '../styles/PageStyles';
import { getKnowledgeBaseByPagination, fetchKnowledgeBaseCount } from '../services/knowledgeBaseService';
import { fetchEntityCount } from '../services/entityService';
import Pagination from '../components/common/Pagination';
import { Network, Search, Plus, Edit2, MessageSquare, Layers, Calendar, Hash, Filter, ChevronDown } from 'lucide-react';
// import { getKnowledgeBaseByPagination, fetchKnowledgeBaseCount } from '../services/knowledgeBaseService';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faLandmark, faPlus } from '@fortawesome/free-solid-svg-icons';
// import { fetchEntityCount } from '../services/entityService';
// import Pagination from '../components/common/Pagination';
import { usePermission } from '../context/PermissionContext';
// Styled Components
const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--primary-500), var(--accent-500));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const HeaderTitle = styled.h1`
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--neutral-900);
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  font-size: var(--text-sm);
  color: var(--neutral-600);
  margin: 0;
  max-width: 700px;
  line-height: 1.5;
`;

const ControlBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;
  max-width: 400px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--neutral-500);
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.75rem 0.625rem 2.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: var(--text-sm);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-500);
    box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
  }
  
  &::placeholder {
    color: var(--neutral-400);
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  font-size: var(--text-sm);
  color: var(--neutral-700);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--primary-500);
    color: var(--primary-500);
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--primary-500), var(--accent-500));
  color: white;
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, var(--primary-600), var(--accent-600));
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 102, 255, 0.25);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const KBGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 1.25rem;
  margin-bottom: 2rem;
`;

const KBCard = styled(motion.div)`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  
  &:hover {
    border-color: rgba(0, 102, 255, 0.3);
    box-shadow: 0 8px 24px rgba(0, 102, 255, 0.12);
    transform: translateY(-3px) scale(1.01);
    
    .card-actions {
      opacity: 1;
    }
  }
`;

const KBCardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const KBCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
`;

const KBIcon = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(0, 102, 255, 0.12), rgba(124, 58, 237, 0.08));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-500);
  box-shadow: 0 2px 8px rgba(0, 102, 255, 0.15);
`;

const KBName = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: var(--neutral-900);
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.active ? 'rgba(16, 185, 129, 0.1)' : '#f3f4f6'};
  color: ${props => props.active ? '#059669' : '#6b7280'};
  border: 1px solid ${props => props.active ? 'rgba(16, 185, 129, 0.2)' : '#e5e7eb'};
`;

const KBDescription = styled.p`
  font-size: var(--text-sm);
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 1rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.8em;
`;

const KBMetadata = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 0.875rem;
  border-top: 1px solid #f3f4f6;
  margin-bottom: 0;
`;

const MetadataLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
  
  svg {
    opacity: 0.7;
  }
`;

const CardActions = styled.div`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  display: flex;
  gap: 0.375rem;
  opacity: 0;
  transition: opacity 0.2s ease;
`;

const IconAction = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${props => props.primary ? 'transparent' : '#e5e7eb'};
  border-radius: 6px;
  background: ${props => props.primary ? 'linear-gradient(135deg, var(--primary-500), var(--accent-500))' : 'white'};
  color: ${props => props.primary ? 'white' : '#6b7280'};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  &:hover {
    background: ${props => props.primary ? 'linear-gradient(135deg, var(--primary-600), var(--accent-600))' : '#f9fafb'};
    border-color: ${props => props.primary ? 'transparent' : 'var(--primary-500)'};
    color: ${props => props.primary ? 'white' : 'var(--primary-500)'};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 102, 255, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border: 2px dashed #e0e0e0;
  border-radius: 12px;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: var(--font-semibold);
  color: var(--neutral-900);
  margin: 0 0 0.5rem 0;
`;

const EmptyDescription = styled.p`
  font-size: var(--text-sm);
  color: var(--neutral-600);
  margin: 0 0 1.5rem 0;
`;

const KnowledgeBase = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [kbList, setKbList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { permissions, permissionEnum, loading } = usePermission();
  

   const userAccessPermissions = permissions?.length
  ? permissions
      .filter(ele => ele.component_type === "knowledgebase")
      .map(ele => ele.permission)
  : [];

  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  const itemsPerPage = 10;


  useEffect(() => {
    const fetchKbData = async () => {
      try {
        const filter = {};
        const sort = {};
        const query = {skip: ( currentPage - 1) * itemsPerPage, limit : itemsPerPage}
        const response = await getKnowledgeBaseByPagination(filter, sort, query);
        const filteredKbs = response.filter(item => (item.name && item.name.trim() !== '')) || [];
        
        // Fetch entity counts for each KB
        const kbsWithCounts = await Promise.all(
          filteredKbs.map(async (kb) => {
            try {
              const count = await fetchEntityCount(kb.code);
              return { ...kb, entityCount: count };
            } catch (error) {
              console.error(`Error fetching entity count for ${kb.code}:`, error);
              return { ...kb, entityCount: 0 };
            }
          })
        );
        
        setKbList(kbsWithCounts);
      } catch (error) {
        console.error('Error fetching user data:', error);
        navigate('/login'); // Redirect to login if unauthorized
      }
    };

    fetchKbData();
  }, [navigate, currentPage]);

  useEffect(() => {
    const getKnowledgeBaseCount = async () => {
      const count = await fetchKnowledgeBaseCount();
      setTotalItems(count);
    }

    getKnowledgeBaseCount();
  }, [])

  // Filter documents based on search term
  const filteredKb = (kbList || []).filter(kb =>
    kb.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kb.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kb.description.toLowerCase().includes(searchTerm.toLowerCase())
  );
  // Handle view claim details
  const handleViewKb = (knowledgebaseCode) => {
    navigate(`/knowledge-base-details-ai/${knowledgebaseCode}`);
  };
  const handleQueryKb = (knowledgebaseId) => {
    navigate(`/knowledge-base-query/${knowledgebaseId}`);
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'active';
      case 'connected': return 'active';
      case 'archived': return 'default';
      case 'disconnected': return 'error';
      default: return 'default';
    }
  };

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        {/* Modern Header */}
        <PageHeader>
          <HeaderTop>
            <HeaderLeft>
                <HeaderIcon>
                <Network size={20} />
              </HeaderIcon>
              <div>
                <HeaderTitle>Knowledge Base</HeaderTitle>
                <HeaderSubtitle>Ontology-driven knowledge repositories powering intelligent agents with structured, interconnected data</HeaderSubtitle>
              </div>
            </HeaderLeft>
          </HeaderTop>
        </PageHeader>

        {/* Control Bar with Search and Filters */}
        <ControlBar>
          <SearchContainer>
            <SearchIcon>
              <Search size={16} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search knowledge bases..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
          
          <FilterButton onClick={() => setFilterStatus(filterStatus === 'all' ? 'active' : 'all')}>
            <Filter size={16} />
            {filterStatus === 'all' ? 'All' : 'Active'}
            <ChevronDown size={14} />
          </FilterButton>
          
          <FilterButton onClick={() => setSortBy(sortBy === 'recent' ? 'name' : 'recent')}>
            Sort: {sortBy === 'recent' ? 'Recent' : 'Name'}
            <ChevronDown size={14} />
          </FilterButton>
          
          <AddButton onClick={() => handleViewKb('new')}>
            <Plus size={18} />
            New Knowledge Base
          </AddButton>
        </ControlBar>

        {/* Card Grid */}
        {filteredKb.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📚</EmptyIcon>
            <EmptyTitle>No Knowledge Bases Yet</EmptyTitle>
            <EmptyDescription>
              Create your first knowledge base to start organizing your data
            </EmptyDescription>
            <AddButton onClick={() => handleViewKb('new')}>
              <Plus size={18} />
              Create Knowledge Base
            </AddButton>
          </EmptyState>
        ) : (
          <KBGrid>
            {filteredKb.map((kb, index) => (
              <KBCard
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <CardActions className="card-actions">
                  <IconAction 
                    onClick={(e) => { e.stopPropagation(); handleViewKb(kb.code); }}
                    title="Edit Knowledge Base"
                  >
                    <Edit2 size={14} />
                  </IconAction>
                  <IconAction 
                    primary 
                    onClick={(e) => { e.stopPropagation(); handleQueryKb(kb.code); }}
                    title="Query Knowledge Base"
                  >
                    <MessageSquare size={14} />
                  </IconAction>
                </CardActions>
                
                <KBCardHeader>
                  <KBCardTitle>
                    <KBIcon>
                      <Layers size={20} />
                    </KBIcon>
                    <KBName>{kb.name}</KBName>
                  </KBCardTitle>
                </KBCardHeader>
                
                <KBDescription>
                  {kb.description || 'No description available'}
                </KBDescription>
                
                <KBMetadata>
                  <MetadataLeft>
                    <MetadataItem>
                      <Calendar size={11} />
                      {formatDate(kb.updated_at)}
                    </MetadataItem>
                    <MetadataItem>•</MetadataItem>
                    <MetadataItem>
                      <Hash size={11} />
                      {kb.entityCount || 0} entities
                    </MetadataItem>
                  </MetadataLeft>
                  <StatusBadge active={true}>Active</StatusBadge>
                </KBMetadata>
              </KBCard>
            ))}
          </KBGrid>
        )}

        {/* <Card style={{ marginTop: '2rem' }}>
          <CardTitle>Knowledge Integration</CardTitle>
          <CardContent>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem auto',
                  fontSize: '1.5rem'
                }}>
                  1
                </div>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Import</h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Upload documents or connect to external sources</p>
              </div>

              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem auto',
                  fontSize: '1.5rem'
                }}>
                  2
                </div>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Process</h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Extract, index, and structure information</p>
              </div>

              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem auto',
                  fontSize: '1.5rem'
                }}>
                  3
                </div>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Validate</h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Verify accuracy and resolve conflicts</p>
              </div>

              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem auto',
                  fontSize: '1.5rem'
                }}>
                  4
                </div>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Deploy</h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Make knowledge available to AI agents</p>
              </div>
            </div>
          </CardContent>
        </Card> */}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalItems}
          setCurrentPage={setCurrentPage}
          setTotalPages={setTotalPages}
        />
      </motion.div>
    </PageContainer>
  );
};

export default KnowledgeBase;
